import React from 'react';

/**
 * ErrorBoundary - Catches React crashes and shows friendly error screen
 * Prevents white screen of death, allows recovery
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    
    // Log error for debugging (could send to error tracking service)
    console.error('ErrorBoundary caught:', error, errorInfo);
    
    // Store error info for support
    try {
      const errorLog = {
        timestamp: new Date().toISOString(),
        error: error?.toString(),
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
        url: window.location.href,
        userAgent: navigator.userAgent
      };
      localStorage.setItem('last_error', JSON.stringify(errorLog));
    } catch (e) {
      // Ignore storage errors
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleClearAndReload = () => {
    // Clear potentially corrupted data
    try {
      localStorage.removeItem('health_portal_session');
      sessionStorage.clear();
    } catch (e) {
      // Ignore
    }
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-lg w-full text-center border border-slate-700">
            <div className="text-6xl mb-4">😵</div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-slate-400 mb-6">
              We hit an unexpected error. Your health data is safe - it&apos;s stored locally on your device.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
              >
                🔄 Try Again
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 px-6 rounded-lg transition"
              >
                🏠 Go to Homepage
              </button>
              
              <button
                onClick={this.handleClearAndReload}
                className="w-full bg-slate-900 hover:bg-slate-800 text-slate-400 font-medium py-2 px-6 rounded-lg transition text-sm"
              >
                🧹 Clear Session & Restart
              </button>
            </div>

            <p className="text-slate-500 text-xs mt-6">
              If this keeps happening, try clearing your browser cache or contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
